// Shared silent-refresh logic for the 1h access token / 30d refresh token pair.
//
// Root cause this file fixes: the access token has always expired after 1h, and a
// working POST /api/auth/refresh endpoint has always existed on the API, but nothing
// in the frontend ever called it. Most of the app also talks to the API via raw
// fetch() calls (not the shared axios client in services/api.js) that silently
// swallow non-OK responses (`if (!res.ok) return;`), so once the token expired the
// UI just stopped receiving fresh data — no redirect, no error, no logout — while
// still "looking" logged in. This module centralises a single, de-duplicated
// refresh-and-retry path used by both the axios client and a global fetch patch
// (see fetchInterceptor.js), plus a proactive background refresh so the token
// rarely reaches expiry during an active session at all.

let refreshPromise = null;

export function forceLogout(reason) {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  try {
    sessionStorage.setItem('sessionExpired', reason || '1');
  } catch (_) { /* ignore */ }
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login?expired=1';
  }
}

// Returns a fresh access token string on success, or null if the session could
// not be refreshed (in which case the user has already been logged out).
export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    forceLogout('no-refresh-token');
    return Promise.resolve(null);
  }

  refreshPromise = fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: storedRefreshToken }),
  })
    .then(async (res) => {
      if (!res.ok) throw new Error('refresh failed: ' + res.status);
      const data = await res.json();
      if (!data || !data.token) throw new Error('refresh response missing token');
      localStorage.setItem('token', data.token);
      return data.token;
    })
    .catch(() => {
      forceLogout('refresh-failed');
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// Proactively refresh the access token well before its 1h expiry so an active
// session never actually hits the expired-token window in the first place.
// This is a belt-and-suspenders measure on top of the reactive 401-triggered
// refresh in fetchInterceptor.js / services/api.js.
const PROACTIVE_REFRESH_INTERVAL_MS = 45 * 60 * 1000; // 45 minutes

export function startProactiveRefresh() {
  setInterval(() => {
    if (localStorage.getItem('token') && localStorage.getItem('refreshToken')) {
      refreshAccessToken();
    }
  }, PROACTIVE_REFRESH_INTERVAL_MS);
}
